import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Loader2, AlertCircle, Building2, RefreshCw } from 'lucide-react';
import { useDebounce } from '../utils/debounce';
import { SearchService } from '../services/searchService';
import { ProjectService } from '../services/projectService';
import ProjectDetailsModal from './ProjectDetailsModal';
import ContactDetailsModal from './ContactDetailsModal';
import ClientDetailsModal from './ClientDetailsModal';
import { formatJobRevNumber } from '../utils/projectFormatting';
import './GlobalSearch.css';


const GlobalSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [refreshingProjectId, setRefreshingProjectId] = useState(null);

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Debounced search function
  const debouncedSearch = useDebounce(async (term) => {
    if (!term || term.trim().length === 0) {
      setProjects([]);
      setClients([]);
      setContacts([]);
      setTotalCount(0);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const { projects: projectResults, clients: clientResults, contacts: contactResults, totalCount: count, error: searchError } = await SearchService.searchAll(term, 10);

    setIsLoading(false);

    if (searchError) {
      setError(searchError);
      setProjects([]);
      setClients([]);
      setContacts([]);
      setTotalCount(0);
    } else {
      setProjects(projectResults || []);
      setClients(clientResults || []);
      setContacts(contactResults || []);
      setTotalCount(count);
      setIsOpen(true);
    }
  }, 300);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedIndex(-1);

    if (value.trim()) {
      debouncedSearch(value);
    } else {
      setProjects([]);
      setClients([]);
      setContacts([]);
      setTotalCount(0);
      setIsOpen(false);
      setError(null);
    }
  };

  // Handle project click
  const handleProjectClick = async (project) => {
    setSelectedProject(project);
    setShowProjectModal(true);
    setIsOpen(false);
  };

  // Handle project update (e.g., status change)
  const handleProjectUpdate = (updatedProject) => {
    // Update the project in the search results
    setProjects(prevProjects =>
      prevProjects.map(p =>
        p.id === updatedProject.id
          ? { ...p, modelingStatus: updatedProject.modelingStatus }
          : p
      )
    );

    // Update the selected project
    setSelectedProject(updatedProject);
  };

  // Handle "Bring Files Back" - toggles stage_modified_refresh false then true
  const handleBringFilesBack = async (e, projectId) => {
    e.stopPropagation();
    setRefreshingProjectId(projectId);
    try {
      await ProjectService.updateProjectBatch(projectId, { stage_modified_refresh: false });
      await ProjectService.updateProjectBatch(projectId, { stage_modified_refresh: true });
    } catch (err) {
      console.error('Error refreshing project files:', err);
    } finally {
      setRefreshingProjectId(null);
    }
  };

  // Handle contact click
  const handleContactClick = async (contact) => {
    setSelectedContact(contact);
    setShowContactModal(true);
    setIsOpen(false);
  };

  // Handle client click
  const handleClientClick = async (client) => {
    setSelectedClient(client);
    setShowClientModal(true);
    setIsOpen(false);
  };


  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const totalResults = projects.length + clients.length + contacts.length;
    if (!isOpen || totalResults === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < totalResults - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : totalResults - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < totalResults) {
          if (selectedIndex < projects.length) {
            // Selected item is a project
            handleProjectClick(projects[selectedIndex]);
          } else if (selectedIndex < projects.length + clients.length) {
            // Selected item is a client
            handleClientClick(clients[selectedIndex - projects.length]);
          } else {
            // Selected item is a contact
            handleContactClick(contacts[selectedIndex - projects.length - clients.length]);
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        searchInputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
    setProjects([]);
    setClients([]);
    setContacts([]);
    setTotalCount(0);
    setIsOpen(false);
    setSelectedIndex(-1);
    setError(null);
    searchInputRef.current?.focus();
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !searchInputRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Highlight search term in text
  const highlightText = (text, term) => {
    if (!text || !term) return text;
    
    const parts = text.split(new RegExp(`(${term})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === term.toLowerCase() ? 
        <mark key={index} className="search-highlight">{part}</mark> : 
        part
    );
  };

  // Get status color
  const getStatusColor = (status) => {
    const statusColors = {
      'Startup': '#6b7280',
      'Data Prep': '#3b82f6',
      'Utilities': '#8b5cf6',
      'Ready to Model': '#06b6d4',
      'Modeling': '#3b82f6',
      'Double Check': '#f97316',
      'Corrections': '#ef4444',
      'Review': '#8b5cf6',
      'Ready to Export': '#10b981',
      'Completed': '#059669'
    };
    return statusColors[status] || '#6b7280';
  };

  return (
    <>
      <div className="global-search-container">
        <div className="global-search-input-wrapper">
          <Search className="search-icon" size={18} />
          <input
            ref={searchInputRef}
            type="text"
            className="global-search-input"
            placeholder="Search projects, contacts, clients, or job numbers..."
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onFocus={() => searchTerm && (projects.length > 0 || clients.length > 0 || contacts.length > 0) && setIsOpen(true)}
          />
          {isLoading && (
            <Loader2 className="search-loading-icon" size={18} />
          )}
          {searchTerm && !isLoading && (
            <button
              className="search-clear-btn"
              onClick={clearSearch}
              aria-label="Clear search"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Search Results Dropdown */}
        {isOpen && (
          <div ref={dropdownRef} className="global-search-dropdown">
            {error ? (
              <div className="search-error">
                <AlertCircle size={16} />
                <span>Error: {error}</span>
              </div>
            ) : projects.length === 0 && clients.length === 0 && contacts.length === 0 ? (
              <div className="search-no-results">
                No projects, clients, or contacts found for "{searchTerm}"
              </div>
            ) : (
              <>
                <div className="search-results-list">
                  {/* Projects Section */}
                  {projects.length > 0 && (
                    <>
                      <div className="search-section-header">Projects</div>
                      {projects.map((project, index) => (
                        <div
                          key={`project-${project.id}`}
                          className={`search-result-item ${
                            selectedIndex === index ? 'selected' : ''
                          }`}
                          onClick={() => handleProjectClick(project)}
                          onMouseEnter={() => setSelectedIndex(index)}
                        >
                          <div className="search-result-main">
                            <span
                              className="search-result-status-dot"
                              style={{ backgroundColor: getStatusColor(project.modelingStatus) }}
                              title={project.modelingStatus}
                            />
                            <div className="search-result-text">
                              <div className="search-result-name">
                                {highlightText(project.projectName, searchTerm)}
                              </div>
                              <div className="search-result-details">
                                {project.jobNumber && (
                                  <>
                                    <span className="search-result-job">
                                      {highlightText(formatJobRevNumber(project.jobNumber, project.rev), searchTerm)}
                                    </span>
                                    {project.clientName && ' • '}
                                  </>
                                )}
                                {project.clientName && (
                                  <span className="search-result-client">
                                    {highlightText(project.clientName, searchTerm)}
                                  </span>
                                )}
                              </div>
                            </div>
                            {project.modelingStatus === 'Completed' &&
                              project.lastStageChangeDate &&
                              (new Date() - new Date(project.lastStageChangeDate)) / 86400000 > 90 && (
                              <button
                                className="search-result-bring-files-btn"
                                onClick={(e) => handleBringFilesBack(e, project.id)}
                                disabled={refreshingProjectId === project.id}
                                title="Bring Files Back"
                              >
                                {refreshingProjectId === project.id
                                  ? <Loader2 size={13} className="search-result-refresh-spin" />
                                  : <RefreshCw size={13} />
                                }
                                Bring Files Back
                              </button>
                            )}
                            <span
                              className="search-result-status-badge"
                              style={{ backgroundColor: getStatusColor(project.modelingStatus) }}
                            >
                              {project.modelingStatus || 'No Status'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Clients Section */}
                  {clients.length > 0 && (
                    <>
                      <div className="search-section-header">Clients</div>
                      {clients.map((client, index) => (
                        <div
                          key={`client-${client.id}`}
                          className={`search-result-item client-result ${
                            selectedIndex === (projects.length + index) ? 'selected' : ''
                          }`}
                          onClick={() => handleClientClick(client)}
                          onMouseEnter={() => setSelectedIndex(projects.length + index)}
                        >
                          <div className="search-result-main">
                            <span className="search-result-client-icon">
                              <Building2 size={18} />
                            </span>
                            <div className="search-result-text">
                              <div className="search-result-name">
                                {highlightText(client.name, searchTerm)}
                              </div>
                              <div className="search-result-details">
                                {client.clientStatus && (
                                  <>
                                    <span className="search-result-status">
                                      {client.clientStatus}
                                    </span>
                                  </>
                                )}
                                {client.address && (
                                  <>
                                    {client.clientStatus && ' • '}
                                    <span className="search-result-address">
                                      {highlightText(client.address, searchTerm)}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <span className="search-result-client-badge">
                              Client
                            </span>
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Contacts Section */}
                  {contacts.length > 0 && (
                    <>
                      <div className="search-section-header">Contacts</div>
                      {contacts.map((contact, index) => (
                        <div
                          key={`contact-${contact.id}`}
                          className={`search-result-item contact-result ${
                            selectedIndex === (projects.length + clients.length + index) ? 'selected' : ''
                          }`}
                          onClick={() => handleContactClick(contact)}
                          onMouseEnter={() => setSelectedIndex(projects.length + clients.length + index)}
                        >
                          <div className="search-result-main">
                            <span className="search-result-contact-avatar">
                              👤
                            </span>
                            <div className="search-result-text">
                              <div className="search-result-name">
                                {highlightText(contact.name, searchTerm)}
                              </div>
                              <div className="search-result-details">
                                {contact.role && (
                                  <>
                                    <span className="search-result-role">
                                      {highlightText(contact.role, searchTerm)}
                                    </span>
                                    {contact.clientName && ' at '}
                                  </>
                                )}
                                {contact.clientName && (
                                  <span className="search-result-client">
                                    {highlightText(contact.clientName, searchTerm)}
                                  </span>
                                )}
                                {contact.email && (
                                  <>
                                    {(contact.role || contact.clientName) && ' • '}
                                    <span className="search-result-email">
                                      {highlightText(contact.email, searchTerm)}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <span className="search-result-contact-badge">
                              Contact
                            </span>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
                {totalCount > (projects.length + clients.length + contacts.length) && (
                  <div className="search-more-results">
                    {totalCount - (projects.length + clients.length + contacts.length)} more result{totalCount - (projects.length + clients.length + contacts.length) !== 1 ? 's' : ''} available
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Project Details Modal */}
      {showProjectModal && selectedProject && (
        <ProjectDetailsModal
          project={selectedProject}
          isOpen={showProjectModal}
          onClose={() => {
            setShowProjectModal(false);
            setSelectedProject(null);
          }}
          onProjectUpdate={handleProjectUpdate}
        />
      )}

      {/* Contact Details Modal */}
      {showContactModal && selectedContact && (
        <ContactDetailsModal
          contact={selectedContact}
          isOpen={showContactModal}
          onClose={() => {
            setShowContactModal(false);
            setSelectedContact(null);
          }}
        />
      )}

      {/* Client Details Modal */}
      {showClientModal && selectedClient && (
        <ClientDetailsModal
          client={selectedClient}
          isOpen={showClientModal}
          onClose={() => {
            setShowClientModal(false);
            setSelectedClient(null);
          }}
          onClientUpdated={(updatedClient) => {
            if (updatedClient) {
              setSelectedClient(updatedClient);
            }
          }}
        />
      )}

    </>
  );
};

export default GlobalSearch;